import { useEffect, useState, useRef } from 'react';
import request from '../../utils/request';
import { useAuthStore, hasFunction } from '../../stores/authStore';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import AlertModal from '../../components/AlertModal/AlertModal';
import CreateFunctionModal from './CreateFunctionModal';
import EditFunctionModal from './EditFunctionModal';
import { MobileResponsiveTable } from '../../components/MobileResponsiveTable';

interface FunctionItem {
  id: number;
  name: string;
  code: string;
  sort: number;
  status: number;
  created_at: string;
  updated_at: string;
}

interface FunctionListResponse {
  items: FunctionItem[];
  total: number;
}

export default function FunctionListPage() {
  const fetchedRef = useRef(false);
  const { user } = useAuthStore();
  const [functions, setFunctions] = useState<FunctionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingFunction, setEditingFunction] = useState<FunctionItem | null>(null);

  // 权限检查
  const canCreate = hasFunction(user, 'function_create');
  const canEdit = hasFunction(user, 'function_edit');
  const canDelete = hasFunction(user, 'function_delete');

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
    type: 'warning' | 'danger' | 'info';
    loading: boolean;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    content: '',
    type: 'warning',
    loading: false,
    onConfirm: () => {},
  });

  // Alert modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    content: '',
    type: 'error',
  });

  const showAlert = (
    title: string,
    content: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'error'
  ) => {
    setAlertModal({
      isOpen: true,
      title,
      content,
      type,
    });
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    // Prevent duplicate requests in React StrictMode
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchFunctions();
  }, []);

  const fetchFunctions = async () => {
    try {
      setLoading(true);
      const response = await request.get<FunctionListResponse>('/gradmin/functions');
      setFunctions(response.data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    fetchFunctions();
  };

  const showConfirm = (
    title: string,
    content: string,
    type: 'warning' | 'danger' | 'info',
    onConfirm: () => void
  ) => {
    setConfirmModal({
      isOpen: true,
      title,
      content,
      type,
      loading: false,
      onConfirm,
    });
  };

  const closeConfirm = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
  };

  const setConfirmLoading = (loading: boolean) => {
    setConfirmModal(prev => ({ ...prev, loading }));
  };

  const handleDelete = (func: FunctionItem) => {
    showConfirm(
      'Delete Function',
      `Are you sure you want to delete function "${func.name}"? This action cannot be undone.`,
      'danger',
      async () => {
        try {
          setConfirmLoading(true);
          await request.delete(`/gradmin/functions/${func.id}`);
          closeConfirm();
          fetchFunctions();
        } catch (err) {
          closeConfirm();
          showAlert('Delete Failed', err instanceof Error ? err.message : 'Delete failed', 'error');
        }
      }
    );
  };

  const handleEdit = (func: FunctionItem) => {
    setEditingFunction(func);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    fetchFunctions();
    setEditingFunction(null);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  return (
    <div className="min-h-full">
      <div className="page-header">
        <h1 className="page-title">Function List</h1>
      </div>

      {/* Action bar */}
      <div className="page-action-bar">
        {canCreate && (
          <button className="page-add-button" onClick={() => setIsModalOpen(true)}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Function
          </button>
        )}
      </div>

      <MobileResponsiveTable
        containerClassName="page-table-container"
        tableClassName="page-data-table"
      >
        <thead>
          <tr>
            <th>Function Name</th>
            <th>Function Code</th>
            <th>Sort</th>
            <th>Created At</th>
            <th>Updated At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={6} className="page-loading-cell">
                <div className="page-loading-spinner"></div>
                Loading...
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan={6} className="text-center py-12 px-4 text-red-600">
                {error}
              </td>
            </tr>
          ) : functions.length === 0 ? (
            <tr>
              <td colSpan={6} className="page-empty-cell">
                No data
              </td>
            </tr>
          ) : (
            functions.map(func => (
              <tr key={func.id}>
                <td>{func.name}</td>
                <td>
                  <code className="font-mono text-xs bg-gray-100 px-2 py-1 rounded text-gray-700">
                    {func.code}
                  </code>
                </td>
                <td>{func.sort}</td>
                <td>{formatDate(func.created_at)}</td>
                <td>{formatDate(func.updated_at)}</td>
                <td>
                  <div className="page-action-buttons">
                    {canEdit && (
                      <button
                        className="page-action-btn page-action-edit"
                        onClick={() => handleEdit(func)}
                      >
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="page-action-btn page-action-delete"
                        onClick={() => handleDelete(func)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </MobileResponsiveTable>

      <CreateFunctionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditFunctionModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingFunction(null);
        }}
        onSuccess={handleEditSuccess}
        functionData={editingFunction}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        content={confirmModal.content}
        type={confirmModal.type}
        loading={confirmModal.loading}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        content={alertModal.content}
        type={alertModal.type}
        confirmText="OK"
        onConfirm={closeAlert}
      />
    </div>
  );
}
